// ABOUTME: Expanded per-member breakdown under a virtual shop's task or stock row.
// ABOUTME: Shows each member shop's shortage, stock and depletion; doubles as the transport split.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import { Home as HomeIcon } from '@mui/icons-material';
import { colors } from '../../theme/colors';
import { formatTime } from '../../utils/formatters';

const TASK_COLUMNS = '3fr 1fr 1fr 1fr 1fr';
const STOCK_COLUMNS = '3fr 1fr 1fr 1fr 1.5fr';

function MemberBreakdown({ members, parentName, variant = 'task', formatTransaction }) {
  const { t } = useTranslation();
  const gridColumns = variant === 'task' ? TASK_COLUMNS : STOCK_COLUMNS;

  const headerCell = (label, align = 'center') => (
    <Box sx={{ textAlign: align, fontSize: '0.7rem', fontWeight: 600, color: colors.text.muted, textTransform: 'uppercase' }}>
      {label}
    </Box>
  );

  const depletesText = (member) => {
    if (member.forecast_error) return t('dashboard.schedule.members.noForecast');
    if (member.is_past_closing) return t('dashboard.schedule.members.pastClosing');
    if (member.stock_depletes_at) return formatTime(member.stock_depletes_at);
    return '-';
  };

  const numberOrDash = (value) => (value != null ? Math.round(value) : '-');

  return (
    <Box
      onClick={(e) => e.stopPropagation()}
      sx={{
        gridColumn: '1 / -1',
        bgcolor: colors.background.hover,
        borderBottom: `1px solid ${colors.border.lighter}`,
        px: 4,
        py: 1,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: gridColumns, py: 0.5 }}>
        {headerCell(t('dashboard.schedule.members.shop'), 'left')}
        {variant === 'task' ? (
          <>
            {headerCell(t('dashboard.schedule.members.demand'))}
            {headerCell(t('dashboard.schedule.members.stock'))}
            {headerCell(t('dashboard.schedule.urgent.depletesAt'))}
            {headerCell(t('dashboard.schedule.members.transport'))}
          </>
        ) : (
          <>
            {headerCell(t('dashboard.schedule.members.stock'))}
            {headerCell(t('dashboard.schedule.members.demand'))}
            {headerCell(t('dashboard.schedule.urgent.depletesAt'))}
            {headerCell(t('stock.lastTransaction'))}
          </>
        )}
      </Box>

      {members.map((member) => {
        const inactive = member.forecast_error || member.is_past_closing;
        const showProductName = member.product_name && parentName && member.product_name !== parentName;
        return (
          <Box
            key={member.shop_id}
            sx={{
              display: 'grid',
              gridTemplateColumns: gridColumns,
              alignItems: 'center',
              py: 0.75,
              px: 0,
              bgcolor: member.is_host ? colors.background.light : 'transparent',
              borderRadius: '6px',
              opacity: inactive ? 0.6 : 1,
            }}
          >
            {/* Shop name (+ member's own product name when it differs) */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
              {member.is_host && (
                <HomeIcon sx={{ fontSize: '0.9rem', color: colors.text.secondary, flexShrink: 0 }} titleAccess={t('dashboard.schedule.members.host')} />
              )}
              <Box sx={{ minWidth: 0 }}>
                <Box sx={{ fontSize: '0.8rem', fontWeight: member.is_host ? 700 : 500, color: colors.text.primary }}>
                  {member.shop_name || member.shop_id}
                </Box>
                {showProductName && (
                  <Box sx={{ fontSize: '0.7rem', color: colors.status.warning, fontStyle: 'italic' }}>
                    {member.product_name}
                  </Box>
                )}
              </Box>
            </Box>

            {variant === 'task' ? (
              <>
                <Box sx={{ textAlign: 'center', fontSize: '0.8rem', color: colors.text.primary }}>
                  {numberOrDash(member.net_demand)}
                </Box>
                <Box sx={{ textAlign: 'center', fontSize: '0.8rem', color: (member.current_stock ?? 0) < 0 ? colors.status.error : colors.text.primary }}>
                  {numberOrDash(member.current_stock)}
                </Box>
                <Box sx={{ textAlign: 'center', fontSize: '0.8rem', color: colors.text.secondary }}>
                  {depletesText(member)}
                </Box>
                {/* The delivery quantity is what the driver acts on - oversized for readability */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', gap: 0.5 }}>
                  <Box component="span" sx={{ fontSize: '1.25rem', fontWeight: 800, lineHeight: 1, color: (member.delivery_quantity ?? 0) > 0 ? colors.text.primary : colors.text.muted }}>
                    {numberOrDash(member.delivery_quantity)}
                  </Box>
                  <Box component="span" sx={{ fontSize: '0.7rem', fontWeight: 600, color: colors.text.muted }}>
                    {t('common.unitsShort')}
                  </Box>
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ textAlign: 'center', fontSize: '0.8rem', fontWeight: 600, color: (member.stock ?? 0) < 0 ? colors.status.error : colors.text.primary }}>
                  {numberOrDash(member.stock)}
                </Box>
                <Box sx={{ textAlign: 'center', fontSize: '0.8rem', color: colors.text.primary }}>
                  {numberOrDash(member.expected_demand)}
                </Box>
                <Box sx={{ textAlign: 'center', fontSize: '0.8rem', color: colors.text.secondary }}>
                  {depletesText(member)}
                </Box>
                <Box sx={{ textAlign: 'center', fontSize: '0.75rem', color: colors.text.secondary }}>
                  {formatTransaction ? formatTransaction(member.last_transaction) : formatTime(member.last_transaction)}
                </Box>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

export default MemberBreakdown;
